import React, { useState, useRef, useEffect } from 'react';
import { Plus, Image, Video, ExternalLink, Trash2, X, ChevronLeft, ChevronRight, Play, AlertCircle } from 'lucide-react';

const FeedGenerator = () => {
    const [urlInput, setUrlInput] = useState('');
    const [allFeedItems, setAllFeedItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);


    const itemsPerPage = 20;
    const totalPages = Math.ceil(allFeedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = allFeedItems.slice(startIndex, startIndex + itemsPerPage);

    // Function to detect platform type
    const detectPlatform = (url) => {
        if (url.includes('instagram.com')) return 'instagram';
        if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
        if (url.includes('vimeo.com')) return 'vimeo';
        if (url.includes('tiktok.com')) return 'tiktok';

        const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
        if (videoExtensions.some(ext => url.toLowerCase().includes(ext))) return 'video';

        return 'image';
    };

    // Function to get Instagram post ID from URL
    const getInstagramPostId = (url) => {
        const patterns = [
            /instagram\.com\/p\/([a-zA-Z0-9_-]+)/,
            /instagram\.com\/reel\/([a-zA-Z0-9_-]+)/,
            /instagram\.com\/tv\/([a-zA-Z0-9_-]+)/
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) return match[1];
        }
        return null;
    };

    // Function to get Instagram oEmbed data
    const getInstagramEmbed = async (url) => {
        try {
            const oembedUrl = `https://graph.instagram.com/oembed?url=${encodeURIComponent(url)}&access_token=`;
            // Since we can't make direct API calls, we'll construct the embed manually
            const postId = getInstagramPostId(url);
            if (!postId) return null;

            return {
                html: `<blockquote class="instagram-media" data-instgrm-permalink="${url}" data-instgrm-version="14"></blockquote>`,
                thumbnail_url: `https://instagram.com/p/${postId}/media/`,
                postId: postId
            };
        } catch (error) {
            console.error('Instagram embed error:', error);
            return null;
        }
    };

    // Enhanced function to extract video ID and create embed URL
    const getVideoInfo = (url) => {
        let videoId = null;
        let platform = detectPlatform(url);
        let embedUrl = url;
        let thumbnailUrl = null;

        if (platform === 'youtube') {
            if (url.includes('youtube.com/watch')) {
                videoId = url.split('v=')[1]?.split('&')[0];
            } else if (url.includes('youtu.be/')) {
                videoId = url.split('youtu.be/')[1]?.split('?')[0];
            }
            if (videoId) {
                embedUrl = `https://www.youtube.com/embed/${videoId}`;
                thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
            }
        } else if (platform === 'vimeo') {
            videoId = url.split('vimeo.com/')[1]?.split('?')[0];
            if (videoId) {
                embedUrl = `https://player.vimeo.com/video/${videoId}`;
            }
        }

        return { videoId, platform, embedUrl, thumbnailUrl };
    };

    // Function to validate URL
    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    // Function to check if image can be loaded
    const checkImageLoad = (url) => {
        return new Promise((resolve) => {
            const img = document.createElement('img');
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
            setTimeout(() => resolve(false), 10000);
        });
    };

    const createFeed = async () => {
        if (!urlInput.trim()) {
            setError('Please enter at least one URL');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const urls = urlInput
                .split(',')
                .map(url => url.trim())
                .filter(url => url.length > 0);

            // Validate URLs
            const invalidUrls = urls.filter(url => !isValidUrl(url));
            if (invalidUrls.length > 0) {
                setError(`Invalid URLs found: ${invalidUrls.join(', ')}`);
                setIsLoading(false);
                return;
            }

            const newFeedItems = await Promise.all(
                urls.map(async (url, index) => {
                    const platform = detectPlatform(url);
                    const isVideo = ['youtube', 'vimeo', 'tiktok', 'video'].includes(platform);
                    const videoInfo = isVideo ? getVideoInfo(url) : null;

                    let thumbnailUrl = null;
                    let canLoadDirectly = true;
                    let embedData = null;

                    if (platform === 'instagram') {
                        embedData = await getInstagramEmbed(url);
                        const postId = getInstagramPostId(url);
                        thumbnailUrl = `https://www.instagram.com/p/${postId}/media/?size=m`;
                        canLoadDirectly = false; // Instagram requires special handling
                    } else if (isVideo && videoInfo?.thumbnailUrl) {
                        canLoadDirectly = await checkImageLoad(videoInfo.thumbnailUrl);
                        if (canLoadDirectly) {
                            thumbnailUrl = videoInfo.thumbnailUrl;
                        }
                    } else if (platform === 'image') {
                        canLoadDirectly = await checkImageLoad(url);
                    }

                    return {
                        id: Date.now() + index,
                        url: url,
                        type: platform === 'instagram' ? 'instagram' : (isVideo ? 'video' : 'image'),
                        title: `${platform.charAt(0).toUpperCase() + platform.slice(1)} ${allFeedItems.length + index + 1}`,
                        embedUrl: videoInfo?.embedUrl || url,
                        thumbnailUrl: thumbnailUrl,
                        platform: platform,
                        videoId: videoInfo?.videoId,
                        canLoadDirectly: canLoadDirectly,
                        embedData: embedData,
                        timestamp: new Date().toLocaleString()
                    };
                })
            );

            setAllFeedItems(prev => [...prev, ...newFeedItems]);
            setUrlInput('');
            setCurrentPage(1);
        } catch (err) {
            setError('Failed to create feed. Please check your URLs and try again.');
            console.error('Feed creation error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const removeFeedItem = (id) => {
        setAllFeedItems(prev => prev.filter(item => item.id !== id));
        const newTotalPages = Math.ceil((allFeedItems.length - 1) / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
        }
    };

    const clearAll = () => {
        setAllFeedItems([]);
        setUrlInput('');
        setError('');
        setCurrentPage(1);
        setIsModalOpen(false);
        setSelectedMedia(null);
    };

    const openModal = (item) => {
        setSelectedMedia(item);
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedMedia(null);
        document.body.style.overflow = 'unset';
    };

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const goToPage = (page) => {
        setCurrentPage(page);
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isModalOpen) {
                closeModal();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isModalOpen]);

    // Load Instagram embed script
    useEffect(() => {
        const script = document.createElement('script');
        script.async = true;
        script.src = '//www.instagram.com/embed.js';
        document.body.appendChild(script);

        return () => {
            // Cleanup
            const existingScript = document.querySelector('script[src="//www.instagram.com/embed.js"]');
            if (existingScript) {
                document.body.removeChild(existingScript);
            }
        };
    }, []);

    const InstagramEmbed = ({ item, isModal = false }) => {
        const embedRef = useRef(null);
        const [isLoaded, setIsLoaded] = useState(false);

        useEffect(() => {
            if (embedRef.current && window.instgrm) {
                window.instgrm.Embeds.process();
                setIsLoaded(true);
            }
        }, [item.url]);

        const postId = getInstagramPostId(item.url);

        return (
            <div ref={embedRef} className={`w-full ${isModal ? 'max-w-lg mx-auto' : 'h-full'}`}>
                <blockquote
                    className="instagram-media"
                    data-instgrm-permalink={item.url}
                    data-instgrm-version="14"
                    style={{
                        background: '#FFF',
                        border: 0,
                        borderRadius: '3px',
                        boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
                        margin: '1px',
                        maxWidth: '100%',
                        minWidth: '326px',
                        padding: 0,
                        width: isModal ? 'auto' : '100%'
                    }}
                >
                    <div style={{ padding: '16px' }}>
                        <a
                            href={item.url}
                            style={{
                                background: '#FFFFFF',
                                lineHeight: 0,
                                padding: '0 0',
                                textAlign: 'center',
                                textDecoration: 'none',
                                width: '100%'
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <div style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center'
                            }}>
                                <div style={{
                                    backgroundColor: '#F4F4F4',
                                    borderRadius: '50%',
                                    flexGrow: 0,
                                    height: '40px',
                                    marginRight: '14px',
                                    width: '40px'
                                }}></div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1,
                                    justifyContent: 'center'
                                }}>
                                    <div style={{
                                        backgroundColor: '#F4F4F4',
                                        borderRadius: '4px',
                                        flexGrow: 0,
                                        height: '14px',
                                        marginBottom: '6px',
                                        width: '100px'
                                    }}></div>
                                    <div style={{
                                        backgroundColor: '#F4F4F4',
                                        borderRadius: '4px',
                                        flexGrow: 0,
                                        height: '14px',
                                        width: '60px'
                                    }}></div>
                                </div>
                            </div>
                            <div style={{ padding: '19% 0' }}></div>
                            <div style={{
                                display: 'block',
                                height: '50px',
                                margin: '0 auto 12px',
                                width: '50px'
                            }}>
                                <svg width="50px" height="50px" viewBox="0 0 60 60" version="1.1">
                                    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                        <g transform="translate(-511.000000, -20.000000)" fill="#000000">
                                            <g>
                                                <path d="M556.869,30.41 C554.814,30.41 553.148,32.076 553.148,34.131 C553.148,36.186 554.814,37.852 556.869,37.852 C558.924,37.852 560.59,36.186 560.59,34.131 C560.59,32.076 558.924,30.41 556.869,30.41 M541,60.657 C535.114,60.657 530.342,55.887 530.342,50 C530.342,44.114 535.114,39.342 541,39.342 C546.887,39.342 551.658,44.114 551.658,50 C551.658,55.887 546.887,60.657 541,60.657 M541,33.886 C532.1,33.886 524.886,41.1 524.886,50 C524.886,58.899 532.1,66.113 541,66.113 C549.9,66.113 557.115,58.899 557.115,50 C557.115,41.1 549.9,33.886 541,33.886 M565.378,62.101 C565.244,65.022 564.756,66.606 564.346,67.663 C563.803,69.06 563.154,70.057 562.106,71.106 C561.058,72.155 560.06,72.803 558.662,73.347 C557.607,73.757 556.021,74.244 553.102,74.378 C549.944,74.521 548.997,74.552 541,74.552 C533.003,74.552 532.056,74.521 528.898,74.378 C525.979,74.244 524.393,73.757 523.338,73.347 C521.94,72.803 520.942,72.155 519.894,71.106 C518.846,70.057 518.197,69.06 517.654,67.663 C517.244,66.606 516.755,65.022 516.623,62.101 C516.479,58.943 516.448,57.996 516.448,50 C516.448,42.003 516.479,41.056 516.623,37.899 C516.755,34.978 517.244,33.391 517.654,32.338 C518.197,30.938 518.846,29.942 519.894,28.894 C520.942,27.846 521.94,27.196 523.338,26.654 C524.393,26.244 525.979,25.756 528.898,25.623 C532.057,25.479 533.004,25.448 541,25.448 C548.997,25.448 549.943,25.479 553.102,25.623 C556.021,25.756 557.607,26.244 558.662,26.654 C560.06,27.196 561.058,27.846 562.106,28.894 C563.154,29.942 563.803,30.938 564.346,32.338 C564.756,33.391 565.244,34.978 565.378,37.899 C565.522,41.056 565.552,42.003 565.552,50 C565.552,57.996 565.522,58.943 565.378,62.101 M570.82,37.631 C570.674,34.438 570.167,32.258 569.425,30.349 C568.659,28.377 567.633,26.702 565.965,25.035 C564.297,23.368 562.623,22.342 560.652,21.575 C558.743,20.834 556.562,20.326 553.369,20.18 C550.169,20.033 549.148,20 541,20 C532.853,20 531.831,20.033 528.631,20.18 C525.438,20.326 523.257,20.834 521.349,21.575 C519.376,22.342 517.703,23.368 516.035,25.035 C514.368,26.702 513.342,28.377 512.574,30.349 C511.834,32.258 511.326,34.438 511.181,37.631 C511.035,40.831 511,41.851 511,50 C511,58.147 511.035,59.17 511.181,62.369 C511.326,65.562 511.834,67.743 512.574,69.651 C513.342,71.625 514.368,73.296 516.035,74.965 C517.703,76.634 519.376,77.658 521.349,78.425 C523.257,79.167 525.438,79.673 528.631,79.82 C531.831,79.965 532.853,80.001 541,80.001 C549.148,80.001 550.169,79.965 553.369,79.82 C556.562,79.673 558.743,79.167 560.652,78.425 C562.623,77.658 564.297,76.634 565.965,74.965 C567.633,73.296 568.659,71.625 569.425,69.651 C570.167,67.743 570.674,65.562 570.82,62.369 C570.966,59.17 571,58.147 571,50 C571,41.851 570.966,40.831 570.82,37.631"></path>
                                            </g>
                                        </g>
                                    </g>
                                </svg>
                            </div>
                            <div style={{ paddingTop: '8px' }}>
                                <div style={{
                                    color: '#3897f0',
                                    fontFamily: 'Arial,sans-serif',
                                    fontSize: '14px',
                                    fontStyle: 'normal',
                                    fontWeight: 550,
                                    lineHeight: '18px'
                                }}>
                                    View this post on Instagram
                                </div>
                            </div>
                        </a>
                    </div>
                </blockquote>
                {!isLoaded && (
                    <div className="flex items-center justify-center p-4 text-gray-500">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600 mr-2"></div>
                        Loading Instagram post...
                    </div>
                )}
            </div>
        );
    };

    const MediaCard = ({ item }) => {
        const [imageError, setImageError] = useState(false);
        const [isLoaded, setIsLoaded] = useState(false);
        const [retryCount, setRetryCount] = useState(0);

        const handleImageError = () => {
            if (retryCount < 2) {
                setRetryCount(prev => prev + 1);
                setTimeout(() => {
                    setImageError(false);
                    setIsLoaded(false);
                }, 1000);
            } else {
                setImageError(true);
            }
        };

        const getAlternativeThumbnail = () => {
            if (item.platform === 'youtube' && item.videoId) {
                const alternatives = [
                    `https://i.ytimg.com/vi/${item.videoId}/maxresdefault.jpg`,
                    `https://i.ytimg.com/vi/${item.videoId}/hqdefault.jpg`,
                    `https://i.ytimg.com/vi/${item.videoId}/mqdefault.jpg`
                ];
                return alternatives[retryCount] || alternatives[0];
            }
            return item.thumbnailUrl || item.url;
        };

        const getPlatformColor = (platform) => {
            switch (platform) {
                case 'instagram': return 'bg-gradient-to-r from-purple-500 to-pink-500';
                case 'youtube': return 'bg-red-500';
                case 'vimeo': return 'bg-blue-500';
                case 'tiktok': return 'bg-black';
                default: return 'bg-gray-500';
            }
        };

        const getPlatformIcon = (platform) => {
            switch (platform) {
                case 'instagram': return '📷';
                case 'youtube': return '▶️';
                case 'vimeo': return '🎬';
                case 'tiktok': return '🎵';
                case 'video': return '🎥';
                default: return '🖼️';
            }
        };

        return (
            <div
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100 cursor-pointer"
                onClick={() => openModal(item)}
            >
                <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    {item.type === 'instagram' ? (
                        <div className="w-full h-full p-2">
                            <InstagramEmbed item={item} />
                        </div>
                    ) : item.type === 'video' ? (
                        <div className="w-full h-full relative">
                            {item.platform === 'youtube' && !imageError ? (
                                <div className="w-full h-full bg-black flex items-center justify-center relative">
                                    {!isLoaded && (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-800 animate-pulse">
                                            <Video size={48} className="text-gray-400" />
                                        </div>
                                    )}
                                    <img
                                        src={getAlternativeThumbnail()}
                                        alt={item.title}
                                        className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                                        onLoad={() => setIsLoaded(true)}
                                        onError={handleImageError}
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                        <div className="bg-red-600 rounded-full p-4 shadow-lg">
                                            <Play size={32} className="text-white ml-1" fill="currentColor" />
                                        </div>
                                    </div>
                                </div>
                            ) : item.platform === 'vimeo' ? (
                                <div className="w-full h-full bg-black flex items-center justify-center relative">
                                    <div className="w-full h-full bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center">
                                        <div className="text-center text-white">
                                            <Video size={48} className="mx-auto mb-2" />
                                            <p className="text-sm">Vimeo Video</p>
                                        </div>
                                    </div>
                                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                        <div className="bg-blue-500 rounded-full p-4 shadow-lg">
                                            <Play size={32} className="text-white ml-1" fill="currentColor" />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full h-full bg-black flex items-center justify-center relative">
                                    <div className="text-center text-white">
                                        <Video size={48} className="mx-auto mb-2" />
                                        <p className="text-sm">Video File</p>
                                        {imageError && (
                                            <p className="text-xs text-red-300 mt-1">
                                                <AlertCircle size={12} className="inline mr-1" />
                                                Preview unavailable
                                            </p>
                                        )}
                                    </div>
                                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                        <div className="bg-gray-700 rounded-full p-4 shadow-lg">
                                            <Play size={32} className="text-white ml-1" fill="currentColor" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="w-full h-full">
                            {!imageError && item.canLoadDirectly ? (
                                <>
                                    {!isLoaded && (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-200 animate-pulse">
                                            <Image size={48} className="text-gray-400" />
                                        </div>
                                    )}
                                    <img
                                        src={item.url}
                                        alt={item.title}
                                        className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                                        onLoad={() => setIsLoaded(true)}
                                        onError={() => setImageError(true)}
                                        loading="lazy"
                                    />
                                </>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                    <div className="text-center text-gray-500">
                                        <Image size={48} className="mx-auto mb-2" />
                                        <p className="text-sm">
                                            {!item.canLoadDirectly ? 'Image blocked by CORS' : 'Image failed to load'}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">Click to open original</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="absolute top-3 left-3">
                        <div className={`${getPlatformColor(item.platform)} text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1`}>
                            <span>{getPlatformIcon(item.platform)}</span>
                            {item.platform.toUpperCase()}
                        </div>
                    </div>

                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-end justify-end p-3">
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white text-gray-800 p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                                title="Open original"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ExternalLink size={16} />
                            </a>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeFeedItem(item.id);
                                }}
                                className="bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                                title="Remove"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-4">
                    <h3 className="font-semibold text-gray-800 mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-500 mb-2">{item.timestamp}</p>
                    <p className="text-xs text-gray-400 truncate font-mono bg-gray-50 px-2 py-1 rounded">
                        {item.url}
                    </p>
                </div>
            </div>
        );
    };

    const Modal = ({ item, onClose }) => {
        if (!item) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4" onClick={onClose}>
                <div className="relative max-w-6xl max-h-[90vh] w-full h-full flex items-center justify-center">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 bg-white bg-opacity-20 text-white p-2 rounded-full hover:bg-opacity-30 transition-all z-10 backdrop-blur-sm"
                    >
                        <X size={24} />
                    </button>

                    <div className="w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        {item.type === 'instagram' ? (
                            <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-auto">
                                <InstagramEmbed item={item} isModal={true} />
                            </div>
                        ) : item.type === 'video' ? (
                            <div className="w-full max-w-4xl aspect-video">
                                {item.platform === 'youtube' || item.platform === 'vimeo' ? (
                                    <iframe
                                        src={item.embedUrl + (item.embedUrl.includes('?') ? '&' : '?') + 'autoplay=1'}
                                        className="w-full h-full rounded-lg shadow-2xl"
                                        frameBorder="0"
                                        allowFullScreen
                                        allow="autoplay; encrypted-media"
                                        title={item.title}
                                    />
                                ) : (
                                    <video
                                        src={item.url}
                                        className="w-full h-full rounded-lg shadow-2xl"
                                        controls
                                        autoPlay
                                        muted
                                    />
                                )}
                            </div>
                        ) : (
                            <img
                                src={item.url}
                                alt={item.title}
                                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const Pagination = () => {
        if (totalPages <= 1) return null;

        const getPageNumbers = () => {
            const delta = 2;
            const range = [];
            const rangeWithDots = [];

            for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
                range.push(i);
            }

            if (currentPage - delta > 2) {
                rangeWithDots.push(1, '...');
            } else {
                rangeWithDots.push(1);
            }

            rangeWithDots.push(...range);

            if (currentPage + delta < totalPages - 1) {
                rangeWithDots.push('...', totalPages);
            } else {
                rangeWithDots.push(totalPages);
            }

            return rangeWithDots;
        };

        return (
            <div className="flex items-center justify-center gap-2 mt-12">
                <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                    <ChevronLeft size={20} />
                </button>

                {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                        <span key={index} className="px-3 py-2 text-gray-500">...</span>
                    ) : (
                        <button
                            key={index}
                            onClick={() => goToPage(page)}
                            className={`px-4 py-2 rounded-lg transition-all ${currentPage === page
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                                : 'bg-white text-gray-700 shadow-md hover:shadow-lg hover:bg-gray-50'
                                }`}
                        >
                            {page}
                        </button>
                    )
                ))}

                <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                    <ChevronRight size={20} />
                </button>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
            <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                Visual Feed Generator
                            </h1>
                            <p className="text-gray-600 mt-2">Create stunning feeds from your image and video URLs</p>
                        </div>
                        {allFeedItems.length > 0 && (
                            <button
                                onClick={clearAll}
                                className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors shadow-lg"
                            >
                                Clear All
                            </button>
                        )}
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
                        <label htmlFor="urls" className="block text-sm font-medium text-gray-700 mb-3">
                            Media URLs (separated by commas)
                        </label>
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <textarea
                                    id="urls"
                                    value={urlInput}
                                    onChange={(e) => setUrlInput(e.target.value)}
                                    placeholder="https://example.com/image1.jpg, https://example.com/video1.mp4, https://youtube.com/watch?v=..., https://instagram.com/p/..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition-all"
                                    rows="3"
                                />
                            </div>
                            <button
                                onClick={createFeed}
                                disabled={isLoading}
                                className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg flex items-center gap-2 font-medium"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Plus size={20} />
                                        Create Feed
                                    </>
                                )}
                            </button>
                        </div>
                        {error && (
                            <p className="mt-3 text-red-600 text-sm bg-red-50 px-4 py-2 rounded-lg">{error}</p>
                        )}
                        <div className="mt-4 text-sm text-gray-600 bg-blue-50 px-4 py-3 rounded-lg">
                            <p className="font-medium mb-2">✨ Supported platforms:</p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <p>📷 Instagram posts & reels</p>
                                <p>▶️ YouTube videos</p>
                                <p>🎬 Vimeo videos</p>
                                <p>🖼️ Direct image URLs</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-8">
                {allFeedItems.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md mx-auto">
                            <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                                <Image size={40} className="text-indigo-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">No Content Yet</h3>
                            <p className="text-gray-600 mb-6">
                                Add some image or video URLs above to create your beautiful visual feed
                            </p>
                            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
                                <p className="font-medium mb-2">Supported formats:</p>
                                <p>• Images: JPG, PNG, GIF, WebP</p>
                                <p>• Videos: MP4, WebM, YouTube, Vimeo</p>
                                <p>• Social: Instagram posts & reels</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-2xl font-bold text-gray-800">
                                Your Feed ({allFeedItems.length} items)
                            </h2>
                            <div className="flex items-center gap-4">
                                <div className="bg-white px-4 py-2 rounded-full shadow-lg border border-gray-200">
                                    <span className="text-sm text-gray-600">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                </div>
                                <div className="bg-white px-4 py-2 rounded-full shadow-lg border border-gray-200">
                                    <span className="text-sm text-gray-600">
                                        {allFeedItems.filter(item => item.type === 'image').length} images, {' '}
                                        {allFeedItems.filter(item => item.type === 'video').length} videos, {' '}
                                        {allFeedItems.filter(item => item.type === 'instagram').length} Instagram
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {currentItems.map((item) => (
                                <MediaCard key={item.id} item={item} />
                            ))}
                        </div>

                        <Pagination />
                    </>
                )}
            </div>

            {isModalOpen && <Modal item={selectedMedia} onClose={closeModal} />}
        </div>
    );
};

export default FeedGenerator;